import styled from "styled-components";

import { color } from "../../theme/color";
import { Icon } from "../ui/Icon";

/** One colour to choose: its value, its name, and how it looks. */
export type SwatchOption<T extends string> = { value: T; label: string; swatch: string };

type SwatchesProps<T extends string> = { label: string; options: SwatchOption<T>[]; value: T; onChange(value: NoInfer<T>): void };

/** A choice between colours, each a round button with its name below. The chosen one has a check. */
export function Swatches<T extends string>({ label, options, value, onChange }: SwatchesProps<T>) {
    return (
        <Group role="group" aria-label={label}>
            {options.map((option) => (
                <Option key={option.value} aria-pressed={option.value === value} onClick={() => onChange(option.value)}>
                    <Circle style={{ background: option.swatch }}>{option.value === value && <Icon name="check" size={20} />}</Circle>
                    {option.label}
                </Option>
            ))}
        </Group>
    );
}

const Group = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
    gap: 8px;
`;

const Option = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 6px 0;
    border: 0;
    border-radius: 12px;
    background: transparent;
    font-size: 12px;
    color: ${color.textSoft};
    cursor: pointer;

    &[aria-pressed="true"] {
        color: ${color.text};
        font-weight: 600;
    }
`;

const Circle = styled.span`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid ${color.border};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${color.background};

    [aria-pressed="true"] > & {
        border-color: ${color.text};
    }
`;
